import {
  DiscountCalculatorVersion,
  DiscountCombinator,
  type GetResult,
  type Models,
  PackageType,
} from "api/types";
import i18next from "i18next";
import { findPackageListing } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";
import { SINGLE_UNIT_THRESHOLD } from "utils/getDeterministicDiscountedPrice";

export const getProductTitle = (
  productName: string | null | undefined,
  variantName: string | null | undefined,
) => {
  if (!productName && !variantName) {
    return "";
  }

  return productName &&
    variantName &&
    productName.toLocaleLowerCase() != variantName.toLocaleLowerCase()
    ? productName + " - " + variantName
    : (variantName ?? "");
};

export const checkIfListingsHaveConcerns = (listings: GetResult<"/listings"> | undefined) => {
  return !!listings?.some(({ concernIds }) => concernIds.length);
};

export const getMembershipRewardGroupSubheader = (
  billingPeriodsBeforeFirstReward: number | null | undefined,
  billingPeriodInterval: number | null | undefined,
  membershipBillingPeriod?: string | null,
) => {
  if (billingPeriodsBeforeFirstReward === null || billingPeriodsBeforeFirstReward === undefined) {
    return "";
  }

  const timePeriod = billingPeriodsBeforeFirstReward === 0 ? "day" : membershipBillingPeriod;
  const dayNumberWithOrdinal = getNumberWithOrdinal(
    billingPeriodsBeforeFirstReward === 0
      ? 1
      : billingPeriodsBeforeFirstReward * (billingPeriodInterval ?? 1),
  );

  return dayNumberWithOrdinal + " " + timePeriod;
};

export const getNumberWithOrdinal = (n: number | null | undefined) => {
  if (n === null || n === undefined) {
    return "";
  }

  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getProductTitleQuantity = (
  quantity: number | null | undefined,
  packageUnitType: Models<"PackageUnitTypeModel"> | null | undefined,
  removeParenthesis?: boolean,
) => {
  if (!quantity || !packageUnitType) {
    return "";
  }

  if (removeParenthesis && quantity > 1) return `${quantity} ${packageUnitType.pluralUnitType}`;

  return quantity > 1 ? `(${quantity} ${packageUnitType.pluralUnitType})` : "";
};

export const getBookAppointmentAction = (
  url?: string | null | undefined,
  phoneNumber?: string | null | undefined,
  email?: string | null | undefined,
) => {
  if (!url && !phoneNumber && !email) {
    return "";
  }

  return url || (phoneNumber && `tel:${phoneNumber}`) || (email && `mailto:${email}`);
};

export const getPDPBannerDiscountText = (
  discount: Models<"ShopProductDiscountModel"> | undefined,
  type: "Membership" | "Package" | "Treatment" | "Product" | "ProductFromExternalSource",
  listings: Models<"ListingModel">[] = [],
  giftCards?: Models<"DisplayGiftCardListing">[],
  freeGiftPromotion?: Models<"PromotionModel">,
) => {
  if (freeGiftPromotion?.freePackageGroup) {
    return i18next.t("helperService.redeemFreeGiftOffer", {
      offerTitle: freeGiftPromotion.title,
    });
  }

  if (discount) {
    // The server decides whether an offer is buy X get Y and states it on every shop discount it
    // returns; re-deriving it from "has requirements and inclusions" read a plain amount-off with a
    // quantity threshold on the same package ("$10 off Botox when you buy 20") as buy X get Y and
    // printed the wrong sentence. The flag is nullable only because the contract is generated from
    // a C# `bool?`, so anything but an explicit true keeps the ordinary discount copy.
    //
    // The details are required alongside the flag because the buy X get Y sentence names the
    // requirement and inclusion items; callers assemble the pairing themselves (the PDP grafts
    // package-level details onto a listing-level model), and a flag with nothing to name would
    // render an empty banner instead of the discount the client is being offered.
    const statesBuyXGetYWithDetails = discount.isBuyXGetY === true && discount.discount != null;

    if (statesBuyXGetYWithDetails) {
      return getBXGYDiscountText(discount.discount ?? undefined, listings, giftCards);
    }

    const { maxPercentageOff, maxAmountOff, title, isVariable, minimumQuantity } = discount;

    let discountAmount = "";
    if (maxPercentageOff) {
      discountAmount = i18next.t("helperService.percentOffValue", {
        percent: Math.round(maxPercentageOff * 100),
      });
    } else if (maxAmountOff) {
      discountAmount = i18next.t("helperService.amountOffValue", {
        amount: formatCurrency(maxAmountOff),
      });
    }

    if (discountAmount) {
      const offerName = title ?? i18next.t("helperService.defaultOfferName");

      if (type === "Membership") {
        if (maxPercentageOff === 1) {
          return i18next.t("helperService.membershipFirstMonthFree", { offerName });
        }
        return i18next.t("helperService.membershipFirstMonthDiscount", {
          discountAmount,
          offerName,
        });
      }

      if (isVariable) {
        return i18next.t("helperService.itemDiscountUpTo", { discountAmount, offerName });
      }

      // A quantity threshold ("$10 off Botox when you buy 20") is the shopper's side of the bargain, and
      // the plain sentence hides it behind a saving they cannot claim on one unit. Buy X get Y already
      // states its threshold in the reward sentence, so a flag that reached here without details — the one
      // case the branch above lets through — must not have it stated a second, contradictory way.
      const statesAQuantityThreshold =
        discount.isBuyXGetY !== true &&
        typeof minimumQuantity === "number" &&
        minimumQuantity > SINGLE_UNIT_THRESHOLD;

      if (statesAQuantityThreshold) {
        return i18next.t("helperService.itemDiscountWithQuantity", {
          quantity: minimumQuantity,
          discountAmount,
          offerName,
        });
      }

      return i18next.t("helperService.itemDiscount", { discountAmount, offerName });
    }
  }

  return "";
};

export const hasBeautyBankAvailable = (rewardGroup: Models<"MembershipRewardGroupModel">) => {
  if (!rewardGroup) {
    return false;
  }

  return (rewardGroup.beautyBank?.creditAmount ?? 0) > 0;
};

/**
 * How many distinct names an offer's copy may name before it counts the rest.
 *
 * A catalog-wide offer stores one requirement and one inclusion row per package, so an offer open to
 * every treatment reads back as dozens of "or" clauses in a tag that has room for a line. Past this
 * many the clause states how many items qualify instead of listing them; a short set still names
 * them, which is the only way a shopper learns what the offer is actually for. The campaign offer
 * card's sentence reads a longer list as "N qualifying items".
 */
export const MAX_ENUMERATED_OFFER_ITEMS = 3;

type ResolvedInclusion = { name: string; quantityLimit: number | null | undefined };

/**
 * Whether the offer's reward is capped at the value of what was bought.
 *
 * Mirrors the backend's own reading of the rule in `RetrieveOfferSummaryHandler`: the dedicated
 * combinator carries it, and the standalone flag is the older way of asking for the same sentence.
 */
const carriesEqualOrLesserRule = (discount: Models<"DiscountModel">) =>
  discount.requireEqualOrLesserValue === true ||
  discount.requirementCombinator === DiscountCombinator.AnyEqualOrLesser;

const buildInclusionPhrase = (
  discount: Models<"DiscountModel">,
  resolvedInclusions: ResolvedInclusion[],
  inclusionNames: string[],
  orJoiner: string,
) => {
  const distinctNames = new Set(resolvedInclusions.map((inclusion) => inclusion.name));
  const quantityLimits = new Set(resolvedInclusions.map((inclusion) => inclusion.quantityLimit));
  const [quantity] = [...quantityLimits];

  // A collapsed clause can only state one "N of" quantity, so an offer whose rewards carry differing
  // or unstated per-item limits keeps its enumeration rather than claim a limit it does not have.
  if (
    distinctNames.size <= MAX_ENUMERATED_OFFER_ITEMS ||
    quantityLimits.size !== 1 ||
    typeof quantity !== "number"
  ) {
    return inclusionNames.join(orJoiner);
  }

  return carriesEqualOrLesserRule(discount)
    ? i18next.t("helperService.rewardOfEqualOrLesserValue", { quantity })
    : i18next.t("helperService.eligibleItemsCount", { quantity, itemCount: distinctNames.size });
};

export const getBXGYDiscountText = (
  discount?: Models<"DiscountModel">,
  listings?: Models<"ListingModel">[],
  giftCards?: Models<"DisplayGiftCardListing">[],
) => {
  if (
    !discount ||
    !listings?.length ||
    !discount.requirements?.length ||
    !discount.inclusions?.length
  ) {
    return "";
  }

  const { requirements, inclusions } = discount;

  const percentOff = (discount.percentOff * 100).toFixed(0);
  const orJoiner = ` ${i18next.t("helperService.orJoiner")} `;

  const resolvedInclusions = inclusions
    .map((inclusion) => ({
      name:
        findPackageName(inclusion.packageId, inclusion.packageType, listings) ||
        findGiftCardName(inclusion.packageId, giftCards),
      quantityLimit: inclusion.quantityLimit,
    }))
    .filter((inclusion) => Boolean(inclusion.name));

  const inclusionNames = resolvedInclusions.map((inclusion) =>
    i18next.t("helperService.quantityOfName", {
      quantity: inclusion.quantityLimit,
      name: inclusion.name,
    }),
  );

  // Handle multiple requirements (e.g., multiple gift cards)
  const giftCardPrices: number[] = [];
  const packageNames: string[] = [];
  let minimumQuantity = 0;
  let minimumSpendAmount = 0;

  requirements.forEach((req) => {
    const packageName = findPackageName(req.packageId, req.packageType, listings);
    if (packageName) {
      packageNames.push(packageName);
    } else {
      const giftCardPrice = findGiftCardPrice(req.packageId, giftCards);
      if (giftCardPrice) {
        giftCardPrices.push(giftCardPrice);
      }
    }
    minimumQuantity = req.minimumQuantity ?? minimumQuantity;
    minimumSpendAmount = req.minimumSpendAmount ?? minimumSpendAmount;
  });

  let requirementName = "";
  if (packageNames.length > 0) {
    const distinctPackageNames = [...new Set(packageNames)];
    requirementName =
      distinctPackageNames.length > MAX_ENUMERATED_OFFER_ITEMS
        ? i18next.t("helperService.qualifyingItemsCount", {
            itemCount: distinctPackageNames.length,
          })
        : packageNames.join(orJoiner);
  } else if (giftCardPrices.length > 0) {
    const uniquePrices = [...new Set(giftCardPrices)];
    const sortedPrices = uniquePrices.sort((a, b) => a - b);
    const formattedPrices = sortedPrices.map((price) => formatCurrency(price, 0));
    requirementName = i18next.t("helperService.giftCardRequirement", {
      count: formattedPrices.length,
      prices: formattedPrices.join(", "),
    });
  }

  const requirementAction = minimumQuantity
    ? i18next.t("helperService.purchaseQuantityOf", { quantity: minimumQuantity })
    : i18next.t("helperService.spendAmountOn", {
        amount: formatCurrency(minimumSpendAmount ?? 0, 0),
      });

  if (!requirementName || !inclusionNames.length) {
    return "";
  }

  const discountAction =
    (discount.percentOff === 1 && i18next.t("helperService.asComplimentaryGift")) ||
    (discount.amountOff &&
      i18next.t("helperService.atAmountOff", {
        amount: formatCurrency(discount.amountOff, 0),
      })) ||
    i18next.t("helperService.atPercentOff", { percent: percentOff });

  return i18next.t("helperService.bxgyDiscountSentence", {
    requirementAction,
    requirementName,
    inclusionNames: buildInclusionPhrase(discount, resolvedInclusions, inclusionNames, orJoiner),
    discountAction,
  });
};

const findPackageName = (
  packageId: string | null | undefined,
  packageType: Models<"PackageType"> | null | undefined,
  listings: Models<"ListingModel">[] | undefined,
) => {
  if (!packageId || !listings?.length) return "";
  const listing = findPackageListing(packageId, listings);

  const selectedPackageName =
    listing?.packages.find((pkg: Models<"PackageModel">) => pkg.id === packageId)?.name ?? "";

  if (
    packageType === PackageType.ProductFromExternalSource ||
    packageType === PackageType.Product
  ) {
    return `${listing?.title} - ${selectedPackageName}`;
  }

  return selectedPackageName;
};

export const findGiftCardPrice = (
  packageId: string | null | undefined,
  giftCards?: Models<"DisplayGiftCardListing">[],
): number | null => {
  if (!packageId || !giftCards?.length) return null;

  const giftCard = giftCards.find((giftCard) =>
    giftCard.giftCards.some((gc) => gc.packageId === packageId),
  );

  if (!giftCard?.giftCards.length) return null;

  const specificGiftCard = giftCard.giftCards.find((gc) => gc.packageId === packageId);

  return specificGiftCard?.baseCostInCents ?? null;
};

const findGiftCardName = (
  packageId: string | null | undefined,
  giftCards?: Models<"DisplayGiftCardListing">[],
) => {
  if (!packageId || !giftCards?.length) return "";

  const price = findGiftCardPrice(packageId, giftCards);

  if (!price) return "";

  return i18next.t("helperService.giftCardRequirement", {
    count: 1,
    prices: formatCurrency(price, 0),
  });
};
/**
 * Whether this discount uses buy X get Y cart behaviour. Matches the backend's
 * UsesBuyXGetYCartBehavior: V1 is exactly one inclusion plus at least one requirement; V2 is at
 * least one of each, and at least one inclusion is a separate reward item or has a required
 * quantity. Same-set qualifying-item amount-off is not BXGY.
 *
 * A V2 reward picked by gift-card denomination expands to one inclusion per card design, so a
 * platform with four designs has four inclusions — the old "exactly one inclusion" test read
 * those offers as ordinary discounts.
 */
export const isBuyXGetYDiscount = (discount: Models<"DiscountModel"> | null | undefined) => {
  if (!discount) return false;

  const requirements = discount.requirements ?? [];
  const inclusions = discount.inclusions ?? [];

  if (discount.calculatorVersion === DiscountCalculatorVersion.V2) {
    if (requirements.length === 0 || inclusions.length === 0) {
      return false;
    }

    const requirementItemIds = new Set(
      requirements
        .map((requirement) => requirement.packageId ?? requirement.membershipId)
        .filter((itemId): itemId is string => itemId != null),
    );

    if (requirementItemIds.size === 0) {
      return false;
    }

    return inclusions.some((inclusion) => {
      const inclusionItemId = inclusion.packageId ?? inclusion.membershipId;
      return (
        inclusionItemId != null &&
        (!requirementItemIds.has(inclusionItemId) || (inclusion.requiredQuantity ?? 0) > 0)
      );
    });
  }

  return inclusions.length === 1 && requirements.length > 0;
};

/**
 * Whether this package is the *reward* side of a buy X get Y discount — it is an inclusion on a
 * discount that also has requirements.
 *
 * Deliberately looser than {@link isBuyXGetYDiscount}: this asks only for the buy X get Y *shape*
 * (at least one requirement, this package among the inclusions), where `isBuyXGetYDiscount` also
 * demands that some inclusion be a separate item or carry a required quantity. That is on purpose
 * — it mirrors the backend's `GetBuyXGetYDiscountRole`, which decides a package's role from the
 * same shape test, and the two must agree or the cart labels a line differently from the server.
 *
 * The consequence is deliberate too: a same-set qualifying-item amount-off on a gift card matches
 * here but not in `isBuyXGetYDiscount`. Callers that must not treat that as a reward pair this
 * with a `discountTotal > 0` check, so only the line the discount actually landed on locks.
 *
 * A package that is only a *requirement* returns false: in "buy a $100 gift card, get a $25 gift
 * card" the $100 card is an ordinary purchase and must keep the send-to-someone-else flow. So does
 * a package carrying a plain discount, which has no requirements at all.
 */
export const isBuyXGetYRewardPackage = (
  packageId: string | null | undefined,
  discounts: ReadonlyArray<Models<"DiscountModel">> | null | undefined,
): boolean => findBuyXGetYRewardDiscount(packageId, discounts) !== undefined;

/**
 * The buy X get Y discount that gives `packageId` as its reward, matched by the same shape test as
 * {@link isBuyXGetYRewardPackage}. Undefined when none of `discounts` rewards that package.
 */
export const findBuyXGetYRewardDiscount = (
  packageId: string | null | undefined,
  discounts: ReadonlyArray<Models<"DiscountModel">> | null | undefined,
): Models<"DiscountModel"> | undefined => {
  if (!packageId || !discounts?.length) return undefined;

  return discounts.find((discount) => {
    const hasRequirements = (discount.requirements?.length ?? 0) > 0;
    if (!hasRequirements) return false;

    return !!discount.inclusions?.some((inclusion) => inclusion.packageId === packageId);
  });
};

/**
 * How a buy X get Y reward is named in shop copy. A gift-card reward is picked by denomination, so
 * it is named by amount ("$25 Gift Card"); anything else keeps its package name.
 */
export const resolveRewardLabel = (
  discount: Models<"DiscountModel"> | null | undefined,
  giftCards: Models<"DisplayGiftCardListing">[] | undefined,
): string => {
  const inclusion = discount?.inclusions?.[0];
  if (!inclusion) return "";

  const price = findGiftCardPrice(inclusion.packageId, giftCards);
  if (price == null) {
    return inclusion.packageName ?? "";
  }

  return i18next.t("helperService.giftCardAmountName", { amount: formatCurrency(price, 0) });
};
